import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Sidebar from './Sidebar';
import DarkModeToggle from '../DarkModeToggle';
import Footer from './Footer';
import '../../styles/layout/DashboardLayout.css';
import { useAuth } from '../../contexts/AuthContext';

// Routes where a logged-OUT visitor sees no nav at all — keeps Home and the
// informational pages minimal/funnel-focused. Logged-in users always see the
// nav everywhere (handled below), and every other route (Pro Calculator,
// Color Mix AI, Login, Account, Admin) always shows it regardless of auth.
const NAV_HIDDEN_ROUTES_LOGGED_OUT = ['/', '/about-us', '/privacy-policy', '/terms-of-service', '/shop'];

const DashboardLayout = ({
  children, 
  sections, 
  activeSection, 
  setActiveSection, 
  isProCalculator,
  isMetric,
  setIsMetric,
  isMetricWeight,
  setIsMetricWeight,
  isDarkMode,
  setIsDarkMode,
  calculatorVersion,
  setCalculatorVersion,
  sectionCompletionStatus = {},
  noWrapperStyling = false
}) => {
  
  const { user } = useAuth();
  const location = useLocation();
  const showNav = !!user || !NAV_HIDDEN_ROUTES_LOGGED_OUT.includes(location.pathname);
  const [headerHidden, setHeaderHidden] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const lastScrollY = useRef(0);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileMenuOpen]);

  useEffect(() => {
    const handleScroll = () => {
      if (window.innerWidth > 768) return;
      const currentY = window.scrollY;
      if (currentY > lastScrollY.current && currentY > 40) {
        setHeaderHidden(true);
      } else {
        setHeaderHidden(false);
      }
      lastScrollY.current = currentY;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className={`dashboard-layout${showNav ? '' : ' dashboard-layout--no-nav'}`}>
      {showNav && (
        <Sidebar
          sections={sections}
          activeSection={activeSection}
          setActiveSection={setActiveSection}
          isProCalculator={isProCalculator}
          setCalculatorVersion={setCalculatorVersion}
          sectionCompletionStatus={sectionCompletionStatus}
          mobileMenuOpen={mobileMenuOpen}
          setMobileMenuOpen={setMobileMenuOpen}
        />
      )}
      <div className="dashboard-content">
        {/* Sticky Header Container */}
        <div className={`dashboard-sticky-header${headerHidden ? ' header-hidden' : ''}`}>
          {/* Hamburger button — mobile only, and only when there's a nav to toggle */}
          {showNav && (
            <button
              className="hamburger-btn"
              onClick={() => setMobileMenuOpen(o => !o)}
              aria-label="Toggle menu"
            >
              <span /><span /><span />
            </button>
          )}

          <div className="header-right-group" style={{ display: 'flex', alignItems: 'center', gap: '16px', marginLeft: 'auto' }}>
            {/* Nav is hidden and logged out — small utility link so a returning user can still sign in */}
            {!showNav && (
              <Link to="/login" className="nav-hidden-login-link">Login</Link>
            )}

            {/* Dark Mode Toggle — stays visible even when the nav is hidden */}
            <div className="dark-mode-toggle-container" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
               <DarkModeToggle isDark={isDarkMode} onToggle={setIsDarkMode} />
               {process.env.PUBLIC_URL === '/beta' && (
                 <span className="beta-badge">BETA</span>
               )}
            </div>
          </div>
        </div>

        <div className={`content-wrapper ${noWrapperStyling ? 'content-wrapper--account-variant' : ''}`}>
          {children}
        </div>
        <Footer />
      </div>
    </div>
  );
};

export default DashboardLayout;
